/**
 * The state the UI shows for an instance: a port of the backend's InstanceStatus, so that the
 * demo backend derives it by the same rules the real one does. Keep the two in step.
 *
 * Rules, in order of precedence:
 * 1. server not connected for longer than `offlineAfterMs`: server_offline;
 * 2. a change (revision, restart, secret) not acknowledged yet, or nothing observed since its ack:
 *    stuck if acknowledged longer than `stuckAfterMs` ago, updating otherwise;
 * 3. desired revision not applied, acknowledged longer than `stuckAfterMs` ago, no failure: stuck;
 * 4. rollback_failed;
 * 5. rolled_back or failed: failed;
 * 6. stopped: stopped;
 * 7. desired revision not applied, or an apply in progress: updating;
 * 8. running and degraded: running_degraded;
 * 9. running: running;
 * 10. anything else, nothing reported included: unknown.
 */
export const OFFLINE_AFTER_MS = 60_000;
export const STUCK_AFTER_MS = 10 * 60_000;

const FAILURES = ['failed', 'rolled_back', 'rollback_failed'];

/**
 * @param snapshot `{ serverConnectedSince, serverDisconnectedAt, desiredRevisionId,
 *   appliedRevisionId, desiredAckedAt, observedState, observedHealth, observedError, observedAt }`,
 *   times in epoch milliseconds or null
 * @returns {{ state: string, error: string | null }}
 */
export function displayedState(snapshot, now, offlineAfterMs = OFFLINE_AFTER_MS, stuckAfterMs = STUCK_AFTER_MS) {
  if (offlineFor(snapshot, now) > offlineAfterMs) {
    return { state: 'server_offline', error: null };
  }
  const ackedLongAgo = snapshot.desiredAckedAt != null && now - snapshot.desiredAckedAt > stuckAfterMs;
  if (snapshot.desiredRevisionId != null
    && (snapshot.desiredAckedAt == null || snapshot.observedAt == null || snapshot.observedAt < snapshot.desiredAckedAt)) {
    // the observed state predates the change: an old failure or an old "running" is not its outcome
    return { state: ackedLongAgo ? 'stuck' : 'updating', error: null };
  }
  const state = snapshot.observedState;
  const failure = FAILURES.includes(state);
  const pending = (snapshot.desiredRevisionId ?? null) !== (snapshot.appliedRevisionId ?? null);
  if (pending && !failure && state !== 'stopped' && ackedLongAgo) {
    return { state: 'stuck', error: null };
  }
  if (state === 'rollback_failed') {
    return { state: 'rollback_failed', error: snapshot.observedError ?? null };
  }
  if (failure) {
    return { state: 'failed', error: snapshot.observedError ?? null };
  }
  if (state === 'stopped') {
    return { state: 'stopped', error: snapshot.observedError ?? null };
  }
  if (pending || state === 'applying') {
    return { state: 'updating', error: null };
  }
  if (state === 'running') {
    return { state: snapshot.observedHealth === 'degraded' ? 'running_degraded' : 'running', error: null };
  }
  return { state: 'unknown', error: null };
}

function offlineFor(snapshot, now) {
  if (snapshot.serverConnectedSince == null) {
    return Number.POSITIVE_INFINITY;
  }
  if (snapshot.serverDisconnectedAt == null) {
    return 0;
  }
  return now - snapshot.serverDisconnectedAt;
}
